<template>
  <div>
    <slot name="header">
      <div
        class="row"
        v-if="
          title ||
          $slots.title ||
          (filterGroups && filterGroups.length > 0) ||
          (buttons && buttons.length > 0) ||
          $slots.buttons
        "
      >
        <div class="col" v-if="title || $slots.title">
          <slot name="title">
            <h2>{{ title }}</h2>
          </slot>
        </div>
        <div
          class="col-auto text-right"
          v-if="
            (filterGroups && filterGroups.length > 0) ||
            (buttons && buttons.length > 0) ||
            $slots.buttons
          "
        >
          <slot name="filters">
            <div
              class="form-inline d-inline-flex"
              v-for="filterGroup in theFilterGroups"
              :key="filterGroup.propKey"
            >
              <div
                class="form-check ms-2 me-2"
                v-for="option in filterGroup.options"
                :key="option.id"
              >
                <label class="form-check-label">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="filterSelections[filterGroup.id][option.id]"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
          </slot>
          <div class="d-inline-block d-print-none">
            <slot name="buttons" />
          </div>
        </div>
      </div>
    </slot>
    <slot />
    <table
      class="odata table table-sm"
      :class="clickable && rows?.length > 0 ? 'table-hover' : null"
    >
      <colgroup>
        <col v-if="reorderable" style="width: 20px" class="d-print-none" />
        <col v-if="selectable" style="width: 20px" class="d-print-none" />
        <col v-for="column in columns" :key="column.id" :class="column.class" />
        <col
          v-if="actions && actions.length > 0"
          style="width: 50px; textalign: right"
          class="d-print-none"
        />
      </colgroup>
      <thead>
        <tr>
          <alzata-table-column-header
            v-if="reorderable"
            :column="{ id: reorderable, sortable: true }"
            :order="order"
            @click="
              (evt) =>
                handleColumnHeadClick(evt, { id: reorderable, sortable: true })
            "
          />
          <th v-if="selectable" class="d-print-none">
            <input type="checkbox" />
          </th>
          <alzata-table-column-header
            v-for="column in columns"
            :key="column.id"
            :column="column"
            :order="order"
            @click="(evt) => handleColumnHeadClick(evt, column)"
          />
          <th v-if="actions && actions.length > 0" class="d-print-none">
            Actions
          </th>
        </tr>
      </thead>
      <tbody v-if="rows && rows.length === 0">
        <tr class="d-print-none">
          <td :colspan="colCount" class="text-center">
            <em>{{ emptyMessage }}</em>
          </td>
        </tr>
        <tr class="d-none d-print-table-row">
          <td :colspan="printableColCount" class="text-center">
            <em>{{ emptyMessage }}</em>
          </td>
        </tr>
      </tbody>
      <tbody v-if="rows && rows.length > 0">
        <tr v-for="item in rows" :key="item.id" @click="handleRowClick(item)">
          <th
            v-if="reorderable"
            class="d-print-none"
            style="
              background-color: var(--bs-table-hover-bg);
              color: rgba(0, 0, 0, 0.25);
              cursor: grab;
            "
            @click="handleStartDrag"
          >
            <alzata-icon icon="fas/grip-vertical" />
          </th>
          <td v-if="selectable" class="d-print-none">
            <input
              type="checkbox"
              @click="
                (evt) => {
                  evt.stopPropagation();
                }
              "
            />
          </td>
          <td
            v-for="column in columns"
            :key="column.id"
            :style="[{ textAlign: column.align }]"
            :class="column.class"
          >
            <component
              :is="componentFor(column.id)"
              :column="column"
              :item="item"
            />
          </td>
          <td
            v-if="actions && actions.length > 0"
            class="d-print-none text-center"
          >
            <div
              class="btn-group btn-group-sm"
              role="group"
              aria-label="actions"
            >
              <button
                class="btn btn-default"
                v-for="action in actions"
                :key="action.id"
                :disabled="action.disabled && action.disabled(item)"
                :title="
                  action.tooltip &&
                  (typeof action.tooltip === 'function'
                    ? action.tooltip(item)
                    : action.tooltip)
                "
                @click="
                  (evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    action.click(item, evt);
                    return false;
                  }
                "
              >
                <alzata-icon :icon="action.icon(item)" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <slot name="footer">
      <div class="row d-print-none">
        <div class="col" v-if="numPages > 1">
          <alzata-paginator
            :numPages="numPages"
            :pageNum="pageNum"
            @selectPage="handleSelectPage"
            v-if="rows && rows.length > 0"
          />
        </div>
        <div class="col-auto ms-auto" v-if="itemCount > 0">
          <div class="col-form-label">
            {{ pageSize * pageNum + 1 }}-{{
              Math.min(itemCount, pageSize * (pageNum + 1))
            }}
            of {{ itemCount }} record{{ itemCount !== 1 ? "s" : "" }}
          </div>
        </div>
        <div class="col-auto" v-if="itemCount > 0">
          <select id="venue" class="form-select" v-model="pageSize">
            <option value="10" selected>10</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="500">500</option>
            <option value="5000">All</option>
          </select>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import { DateTime } from "luxon";

function camelCase(propName) {
  return propName.replace(/^[A-Z]/, (match) => match.toLowerCase());
}

function dashCase(propName) {
  return propName.replace(
    /[A-Z]/g,
    (match, idx) => `${idx > 0 ? "-" : ""}${match.toLowerCase()}`
  );
}

export default {
  name: "AlzataOdataTable",
  props: {
    title: String,
    endpoint: String,
    clickable: {
      type: Boolean,
      default: false,
    },
    reorderable: {
      type: Boolean,
      default: false,
    },
    columns: {},
    defaultOrder: String,
    buttons: {},
    filterGroups: {
      type: Array,
      default: () => [],
    },
    defaultPageSize: {
      type: Number,
      default: 10,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default: "There are no records to display.",
    },
    actions: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      rows: [],
      order: {
        column: null,
        dir: "asc",
      },
      itemCount: 0,
      numPages: 0,
      pageNum: 0,
      theFilterGroups: [],
      filterSelections: {},
      pageSize: this.defaultPageSize,

      theColumns: [],
    };
  },
  beforeMount() {
    if (this.filterGroups) {
      this.filterSelections = this.filterGroups.reduce((p, c) => {
        p[c.id] = c.options.reduce((a, b) => {
          a[b.id] = false;
          return a;
        }, {});
        return p;
      }, {});
    }
  },
  mounted() {
    if (this.tmp) {
      this.pageNum = this.tmp;
    }

    if (this.defaultOrder) {
      var parts = this.defaultOrder.trim().split(" ", 2);
      if (parts && parts.length > 0) {
        var orderCol = parts[0];
        if ((this.columns || []).filter((x) => x.id === orderCol).length > 0) {
          this.order = {
            column: orderCol,
            dir:
              parts.length > 1 && parts[1].toLowerCase() === "desc"
                ? "desc"
                : "asc",
          };
        }
      }
    }

    if (!this.order.column) {
      var sortableColumn = (this.columns || []).filter((x) => x.sortable)[0];
      if (sortableColumn) {
        this.order = {
          column: sortableColumn.id,
          dir: "asc",
        };
      }
    }
  },
  methods: {
    clearRows(){
      this.rows.splice(0);
    },
    addFilterGroup(filterGroup) {
      this.theFilterGroups.push(filterGroup);
    },
    removeFilterGroup(filterGroup) {
      const idx = this.theFilterGroups.indexOf(filterGroup);
      if (idx >= 0) {
        this.theFilterGroups.splice(idx, 1);
      }
    },

    addColumn(colInfo) {
      this.theColumns.push(colInfo);
    },

    handleSelectPage(page) {
      this.pageNum = page;
    },
    async reload() {
      var data = await this.apiClient.getJson(this.endpoint, {
        query: this.viewOpts,
      });
      this.rows = data.value;
      this.itemCount = data["@odata.count"];

      this.numPages = Math.ceil(this.itemCount / this.pageSize);
    },
    handleStartDrag(evt) {
      evt.preventDefault();
      evt.stopPropagation();
    },
    handleColumnHeadClick(evt, column) {
      if (!column.sortable) {
        return;
      }
      evt.preventDefault();
      if (this.order.column !== column.id) {
        this.order = {
          column: column.id,
          dir: "asc",
        };
      } else {
        this.order.dir = this.order.dir === "asc" ? "desc" : "asc";
      }
    },
    async print(title) {
      const origSize = this.pageSize;
      const origTitle = window.document.title;
      this.pageSize = 5000;
      await this.reload();
      if (title) {
        window.document.title = `${title} - ${origTitle}`;
      }
      window.print();
      window.document.title = origTitle;
      this.pageSize = origSize;
      await this.reload();
    },
    componentFor(propName) {
      const slotName = "format-" + propName.split("/").map(dashCase).join("-");
      return (
        this.$slots[slotName] || {
          props: {
            column: {},
            item: {},
          },
          render() {
            if (this.column.formatter) {
              return this.column.formatter(this.item);
            }
            const parts = this.column.id.split("/").map(camelCase);
            let ob = this.item;
            // let dollarUS = Intl.NumberFormat("en-US", {
            //         style: "currency",
            //         currency: "USD",
            //     });
            for (const part of parts) {
              ob = ob[part];
              if (!ob) {
                return "";
              }
            }
            try {
              // Attempt to process as a date
              const isDate =
                /[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])[T](2[0-3]|[01][0-9]):[0-9][0-9]:[0-9][0-9].*$/i.test(
                  ob
                );
              const dateValue = DateTime.fromISO(ob, { zone: "utc" });
              if (isDate && dateValue && dateValue.isValid) {
                return dateValue
                  .toLocal()
                  .toLocaleString(DateTime.DATETIME_MED);
              }
            } catch {
              // do nothing
            }
            return ob;
          },
        }
      );
    },
    handleRowClick(row) {
      // Don't do anything unless the row is actually clickable
      if (!this.clickable) {
        return;
      }
      const defaultAction = this.actions.filter((x) => x.default)[0];
      if (defaultAction) {
        defaultAction.click(row);
      } else {
        this.$emit("rowClick", row);
      }
    },
  },
  watch: {
    viewOpts() {
      this.reload();
    },
    endpoint() {
      this.reload();
    },
  },
  computed: {
    viewOpts() {
      var rval = {
        $skip: this.pageSize * this.pageNum,
        $top: this.pageSize,
        $count: true,
      };

      if (this.order.column) {
        rval.$orderby = `${this.order.column} ${this.order.dir}`;
      }

      const filter = [
        ...this.theFilterGroups.map((x) => x.filterValue).flat(),
        ...this.filters,
      ].join(" and ");
      if (filter) {
        rval.$filter = filter;
      }

      return rval;
    },
    colCount() {
      return (
        (this.columns || []).length +
        (this.selectable ? 1 : 0) +
        (this.actions ? 1 : 0)
      );
    },
    printableColCount() {
      return (this.columns || []).length;
    },
  },
};
</script>
